import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Plus, RefreshCw, AlertTriangle, MessageSquare, X } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Dialog } from '@/components/ui/Dialog';
import {
  useReclamations, useCreateReclamation, useUpdateReclamation, useDeleteReclamation,
} from '@/hooks/useBetonSprint4';
import { useChantiers } from '@/hooks/useBetonCatalog';
import {
  STATUT_RECLAMATION_LABELS, type StatutReclamation, type ReclamationChantier,
} from '@/types';
import { extractError } from '@/api/client';
import { demander } from '@/components/ui/Fenetres';

const fmtDT = (iso: string) => new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

const STATUT_COLORS: Record<StatutReclamation, string> = {
  ouverte:       'var(--danger)',
  en_traitement: 'var(--warning)',
  resolue:       'var(--success)',
  cloturee:      'var(--tx-3)',
};

export const ReclamationsPage = () => {
  const [statut, setStatut]           = useState<StatutReclamation | ''>('');
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [editFor, setEditFor]         = useState<ReclamationChantier | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isLoading, refetch } = useReclamations({ statut: statut || undefined, limit: 200 });
  const remove = useDeleteReclamation();
  const reclamations = data?.data ?? [];

  const kpis = useMemo(() => ({
    ouverte:       reclamations.filter((r) => r.statut === 'ouverte').length,
    en_traitement: reclamations.filter((r) => r.statut === 'en_traitement').length,
    resolue:       reclamations.filter((r) => r.statut === 'resolue').length,
    cloturee:      reclamations.filter((r) => r.statut === 'cloturee').length,
  }), [reclamations]);

  const handle = async (fn: () => Promise<any>, msg?: string) => {
    if (msg && !(await demander.confirmer({ titre: msg, danger: /supprim|annul|rejet|refus|désactiv|écart/i.test(msg) }))) return;
    setActionError(null);
    try { await fn(); } catch (e) { setActionError(extractError(e).message); }
  };

  return (
    <>
      <Topbar
        title="Réclamations chantier"
        subtitle="Module 9 — Suivi réclamations terrain (livraison · qualité produit · pose) + workflow résolution"
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}>
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            </button>
            <button className="ds-btn ds-btn-p" onClick={() => setDialogOpen(true)}>
              <Plus size={14} /> Nouvelle réclamation
            </button>
          </>
        }
      />
      <div className="ds-content">
        {actionError && (
          <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt">{actionError}</div>
          </div>
        )}

        <div className="ds-kpi-strip">
          <div className="ds-ks ks-red">
            <div className="ds-ks-label">Ouvertes</div>
            <div className="ds-ks-value">{kpis.ouverte}</div>
            <div className="ds-ks-sub">À traiter</div>
          </div>
          <div className="ds-ks ks-orange">
            <div className="ds-ks-label">En traitement</div>
            <div className="ds-ks-value">{kpis.en_traitement}</div>
            <div className="ds-ks-sub">Actions en cours</div>
          </div>
          <div className="ds-ks ks-green">
            <div className="ds-ks-label">Résolues</div>
            <div className="ds-ks-value">{kpis.resolue}</div>
            <div className="ds-ks-sub">Réponse fournie</div>
          </div>
          <div className="ds-ks ks-blue">
            <div className="ds-ks-label">Clôturées</div>
            <div className="ds-ks-value">{kpis.cloturee}</div>
            <div className="ds-ks-sub">Archivées</div>
          </div>
        </div>

        <div className="ds-filters">
          <select className="ds-fi" value={statut} onChange={(e) => setStatut(e.target.value as StatutReclamation | '')} style={{ width: 'auto' }}>
            <option value="">Tous statuts</option>
            {(Object.keys(STATUT_RECLAMATION_LABELS) as StatutReclamation[]).map((s) => (
              <option key={s} value={s}>{STATUT_RECLAMATION_LABELS[s]}</option>
            ))}
          </select>
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">Réclamations</div>
              <div className="ds-table-sub">{reclamations.length} fiche{reclamations.length > 1 ? 's' : ''}</div>
            </div>
          </div>
          <table className="ds-tbl">
            <thead>
              <tr>
                <th style={{ width: 110 }}>Référence</th>
                <th style={{ width: 130 }}>Chantier</th>
                <th>Objet</th>
                <th style={{ width: 90 }}>Gravité</th>
                <th style={{ width: 110 }}>Statut</th>
                <th style={{ width: 110 }}>Ouverte</th>
                <th style={{ width: 110 }}>Cloturée</th>
                <th style={{ width: 90 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32 }}>Chargement…</td></tr>}
              {!isLoading && reclamations.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: 48 }}>
                    <MessageSquare size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                    <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucune réclamation</p>
                  </td>
                </tr>
              )}
              {!isLoading && reclamations.map((r) => (
                <tr key={r.id} onClick={() => setEditFor(r)} style={{ cursor: 'pointer' }}>
                  <td><span className="ds-ref-code">{r.reference}</span></td>
                  <td>
                    <div className="ds-entry-label">{r.chantier?.nom ?? '—'}</div>
                    <div className="ds-entry-sub">{r.chantier?.code}</div>
                  </td>
                  <td style={{ fontSize: 12 }}>
                    <div style={{ fontWeight: 500 }}>{r.objet}</div>
                    {r.description && <div style={{ fontSize: 10, color: 'var(--tx-3)', marginTop: 2 }}>{r.description.slice(0, 80)}{r.description.length > 80 ? '…' : ''}</div>}
                  </td>
                  <td>
                    {r.gravite && (
                      <span className="ds-bd" style={{ background: r.gravite === 'critique' ? 'var(--danger)' : r.gravite === 'moyenne' ? 'var(--warning)' : 'var(--info)', color: 'white', fontSize: 10 }}>
                        {r.gravite}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className="ds-bd" style={{ background: STATUT_COLORS[r.statut], color: 'white', fontSize: 10 }}>
                      {STATUT_RECLAMATION_LABELS[r.statut]}
                    </span>
                  </td>
                  <td style={{ fontSize: 11, color: 'var(--tx-2)' }}>{fmtDT(r.dateOuverture)}</td>
                  <td style={{ fontSize: 11, color: 'var(--tx-3)' }}>{r.dateCloture ? fmtDT(r.dateCloture) : '—'}</td>
                  <td>
                    <button className="ds-btn ds-btn-g ds-btn-sm" onClick={(e) => { e.stopPropagation(); handle(() => remove.mutateAsync(r.id), `Supprimer ${r.reference} ?`); }} style={{ color: 'var(--danger)' }}>
                      <X size={11} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ReclamationFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
      <ReclamationEditDialog reclamation={editFor} onClose={() => setEditFor(null)} />
    </>
  );
};

// ── Dialog création ─────────────────────────────────────

const ReclamationFormDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const [chantierId, setChantierId] = useState('');
  const [objet, setObjet]           = useState('');
  const [description, setDescr]     = useState('');
  const [gravite, setGravite]       = useState('moyenne');
  const [notes, setNotes]           = useState('');
  const [error, setError]           = useState<string | null>(null);

  const { data: chsData } = useChantiers({ limit: 200 });
  const create = useCreateReclamation();
  const chs = chsData?.data ?? [];

  useEffect(() => {
    if (open) { setChantierId(''); setObjet(''); setDescr(''); setGravite('moyenne'); setNotes(''); setError(null); }
  }, [open]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({
        chantierId, objet, description: description.trim() || undefined,
        gravite: gravite.trim() || undefined, notes: notes.trim() || undefined,
      });
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Nouvelle réclamation chantier" subtitle="Réf auto RC-YYYY-NNN · statut initial Ouverte" size="md">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}

        <Field label="Chantier *">
          <select value={chantierId} onChange={(e) => setChantierId(e.target.value)} required className="ds-fi" style={{ width: '100%' }}>
            <option value="">— Sélectionner —</option>
            {chs.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.nom}</option>)}
          </select>
        </Field>

        <Field label="Objet *">
          <input value={objet} onChange={(e) => setObjet(e.target.value)} required className="ds-fi" style={{ width: '100%' }} placeholder="Livraison incomplète — 2 poteaux manquants" />
        </Field>

        <Field label="Description détaillée">
          <textarea value={description} onChange={(e) => setDescr(e.target.value)} rows={3} className="ds-fi" style={{ width: '100%' }} placeholder="Détails, photos, témoins…" />
        </Field>

        <Field label="Gravité">
          <select value={gravite} onChange={(e) => setGravite(e.target.value)} className="ds-fi" style={{ width: '100%' }}>
            <option value="mineure">Mineure</option>
            <option value="moyenne">Moyenne</option>
            <option value="critique">Critique</option>
          </select>
        </Field>

        <Field label="Notes internes">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="ds-fi" style={{ width: '100%' }} />
        </Field>

        <div className="flex justify-end gap-2 border-t pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!chantierId || !objet || create.isPending}>
            <Plus size={14} /> {create.isPending ? 'Création…' : 'Créer réclamation'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

// ── Dialog édition (workflow) ───────────────────────────

const ReclamationEditDialog = ({ reclamation, onClose }: { reclamation: ReclamationChantier | null; onClose: () => void }) => {
  const open = reclamation !== null;
  const update = useUpdateReclamation();
  const [statut, setStatut]       = useState<StatutReclamation>('ouverte');
  const [resolution, setResol]    = useState('');
  const [notes, setNotes]         = useState('');
  const [error, setError]         = useState<string | null>(null);

  useEffect(() => {
    if (reclamation) {
      setStatut(reclamation.statut);
      setResol(reclamation.resolution ?? '');
      setNotes(reclamation.notes ?? '');
      setError(null);
    }
  }, [reclamation]);

  if (!reclamation) return null;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await update.mutateAsync({ id: reclamation.id, payload: { statut, resolution: resolution.trim() || undefined, notes: notes.trim() || undefined } });
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  return (
    <Dialog open={open} onClose={onClose} title={`Réclamation ${reclamation.reference}`} subtitle={`${reclamation.chantier?.nom} · ${reclamation.objet}`} size="md">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}

        {reclamation.description && (
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-xs">
            <div className="font-semibold mb-1">Description</div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{reclamation.description}</div>
          </div>
        )}

        <Field label="Statut">
          <select value={statut} onChange={(e) => setStatut(e.target.value as StatutReclamation)} className="ds-fi" style={{ width: '100%' }}>
            {(Object.keys(STATUT_RECLAMATION_LABELS) as StatutReclamation[]).map((s) => (
              <option key={s} value={s}>{STATUT_RECLAMATION_LABELS[s]}</option>
            ))}
          </select>
        </Field>

        <Field label="Résolution / Action corrective">
          <textarea value={resolution} onChange={(e) => setResol(e.target.value)} rows={3} className="ds-fi" style={{ width: '100%' }} placeholder="Pièce de remplacement livrée le… / refait par équipe X…" />
        </Field>

        <Field label="Notes internes">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="ds-fi" style={{ width: '100%' }} />
        </Field>

        {(statut === 'resolue' || statut === 'cloturee') && !reclamation.dateCloture && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-800">
            ℹ La dateCloture sera horodatée automatiquement.
          </div>
        )}

        <div className="flex justify-end gap-2 border-t pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Fermer</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={update.isPending}>
            {update.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>
    {children}
  </div>
);
